import { IDB } from '@/core/db'
import { findTableIdsInDescendant, isDescendant } from '@/lib/slate/utils'

import { isEmpty } from './utils'

/**
 * InjectableMap is a map that can be injected with a key and a value
 */
class InjectableMap extends Map<string, Set<string>> {
  constructor(initialMap = new Map()) {
    super(initialMap)
  }

  inject(key: string, value: string) {
    if (this.has(key)) {
      this.get(key)?.add(value)
    } else {
      this.set(key, new Set([value]))
    }
  }
}

/**
 * Common utility function to collect table ids from a data object,
 * can be used to collect table ids from a data object (layer settings, component settings, actions, etc)
 *
 * made easier for debugging and injecting table ids into the map.
 *
 * @returns Map<string, Set<string>> - injectable table ids map with entry keys as the value
 *
 */
export function collectTableIds(
  data: Record<string, any> | any[],
  dbClient: IDB | undefined,
  excludedKeys: string[] = [],
  startEntryKey: string = 'entry'
) {
  const initialMap = new InjectableMap()

  if (!dbClient) return initialMap

  const tableIdsMap = initialMap
  const maxSize = dbClient.tablesMap.size

  const findTableIdsRecursive = (entryKey: string, value: any) => {
    if (tableIdsMap.size >= maxSize) return

    switch (true) {
      case isEmpty(value): {
        break
      }
      case isDescendant(value): {
        const tblIds = findTableIdsInDescendant(value)
        tblIds.forEach((tbl) => {
          tableIdsMap.inject(tbl, entryKey)
        })
        break
      }
      case Array.isArray(value): {
        value.forEach((item, index) => findTableIdsRecursive(`${entryKey}.[${index}]`, item))
        break
      }
      case typeof value === 'object' && !isEmpty(value): {
        Object.entries(value).forEach(([k, v]) => findTableIdsRecursive(`${entryKey}.${k}`, v))
        break
      }
      case typeof value === 'string': {
        if (
          // table not found
          !dbClient.tablesMap.has(value) ||
          // excluded key
          excludedKeys.includes(entryKey) ||
          excludedKeys.some((key) => entryKey.includes(key))
        ) {
          break
        }
        tableIdsMap.inject(value, entryKey)
        break
      }
      default: {
        break
      }
    }
  }

  findTableIdsRecursive(startEntryKey, data)

  return tableIdsMap
}


/**
 * Simple utility function to replace table ids in a data object,
 */
export function replaceTableIdsRecursive<T>(
  fromTableId: string,
  toTableId: string,
  data: T,
  excludedKeys: string[] = [],
  startEntryKey = 'entry'
): T {
  const replace = (currentValue: any, entryKey: string): any => {
    if (
      excludedKeys.length > 0 &&
      (excludedKeys.includes(entryKey) || excludedKeys.some((key) => entryKey.includes(key)))
    ) {
      return currentValue
    }

    switch (true) {
      case isEmpty(currentValue): {
        return currentValue
      }
      case Array.isArray(currentValue): {
        return currentValue.map((item, index) => replace(item, `${entryKey}.[${index}]`))
      }
      case typeof currentValue === 'object' && !!currentValue: {
        return Object.fromEntries(
          Object.entries(currentValue).map(([k, v]) => [k, replace(v, `${entryKey}.${k}`)])
        )
      }
      case typeof currentValue === 'string': {
        if (currentValue === fromTableId) {
          return toTableId
        }
        // Handle stringified JSON
        try {
          const parsed = JSON.parse(currentValue)
          // Ensure it's an object or array before trying to replace
          if (typeof parsed === 'object' && parsed !== null) {
            const replaced = replace(parsed, entryKey)
            // Only re-stringify if something actually changed to avoid unnecessary computation
            // and to preserve original string if no replacements were made.
            if (JSON.stringify(parsed) !== JSON.stringify(replaced)) {
              return JSON.stringify(replaced)
            }
          }
        } catch (e) {
          // Not a valid JSON string, so just return it as is.
        }
        return currentValue
      }
      default: {
        return currentValue
      }
    }
  }

  return replace(data, startEntryKey)
}
